import { COMMONS_API_URL, UPLOAD_CHUNK_SIZE, MAX_TITLE_LENGTH } from "./config"
import { getAccessToken, refreshAccessToken } from "./oauth"
import type { UploadForm, UploadResult, LicenseId, LicenseChoice } from "./types"

// All GET requests include maxlag=5 - when Commons replication lag is high,
// the API responds with a 503 + retry-after instead of completing the query,
// so well-behaved tools should let the cluster catch up. POST writes
// (chunkedUpload) include maxlag=5 implicitly via the same query string.
const apiUrl = COMMONS_API_URL + "?crossorigin=&format=json&maxlag=5"

function authHeaders(): Record<string, string> {
  const token = getAccessToken()
  return token ? { Authorization: "Bearer " + token } : {}
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const headers = { ...(init?.headers || {}), ...authHeaders() }
  let resp = await fetch(url, { ...init, headers })
  if (resp.status === 401) {
    const newToken = await refreshAccessToken()
    if (newToken) {
      const retryHeaders = { ...(init?.headers || {}), Authorization: "Bearer " + newToken }
      resp = await fetch(url, { ...init, headers: retryHeaders })
    }
  }
  return resp
}

export interface UserCheckResult {
  name: string
  groups: string[]
  rightsCount: number
  hasUpload: boolean
  /** True if the account has the 'autoconfirmed' implicit group on Commons
   *  (account ≥ 4 days old and ≥ 10 edits). Required to use this tool to
   *  prevent abuse from very fresh accounts. */
  isAutoconfirmed: boolean
  csrfTokenLen: number
  csrfIsAnon: boolean
  csrfToken: string
}

export interface ChunkProgress { ratio: number; uploadedBytes: number; totalBytes: number }

export async function checkUser(): Promise<UserCheckResult> {
  // One MediaWiki API call instead of two: meta=userinfo|tokens fetches
  // both the user record (with groups + rights) and the CSRF token in a
  // single round-trip. Saves ~150-300 ms on first login depending on
  // RTT to commons.wikimedia.org.
  const resp = await authFetch(
    apiUrl + "&action=query&meta=userinfo%7Ctokens&uiprop=groups%7Crights&type=csrf"
  )
  if (!resp.ok) throw new Error("Auth fetch failed: HTTP " + resp.status)
  const json = await resp.json()
  if (json.error) throw new Error("Auth fetch failed: " + (json.error.info || json.error.code))

  const userinfo = json?.query?.userinfo
  if (!userinfo || !userinfo.name) throw new Error("Auth response missing userinfo.")
  if (userinfo.anon !== undefined || userinfo.id === 0) {
    throw new Error("Token authenticated as anonymous. Try logging in again.")
  }
  const csrf = json?.query?.tokens?.csrftoken
  if (!csrf) throw new Error("Auth response missing csrf token.")
  if (csrf === "+\\") throw new Error("Got anonymous CSRF token.")

  const groups: string[] = userinfo.groups || []
  // 'autoconfirmed' is the implicit group MediaWiki adds once the account
  // meets the wiki-configured age + edit-count threshold (4 days / 10 edits
  // on Commons). Sysops/bureaucrats etc. are autoconfirmed transitively.
  const isAutoconfirmed = groups.includes("autoconfirmed") ||
    groups.includes("user") && groups.includes("autoconfirmed") ||
    groups.includes("sysop") ||
    groups.includes("bureaucrat") ||
    groups.includes("bot")

  return {
    name: userinfo.name,
    groups,
    rightsCount: (userinfo.rights || []).length,
    hasUpload: (userinfo.rights || []).includes("upload"),
    isAutoconfirmed,
    csrfTokenLen: csrf.length,
    csrfIsAnon: false,
    csrfToken: csrf
  }
}

export function sanitizeTitle(input: string): string {
  let t = input
  // eslint-disable-next-line no-control-regex
  t = t.replace(/[\x00-\x1F\x7F]/g, "")
  t = t.replace(/[#<>[\]|{}/\\]/g, "")
  t = t.replace(/\s+/g, " ").trim()
  if (t.length > MAX_TITLE_LENGTH) t = t.substring(0, MAX_TITLE_LENGTH).trim()
  return t
}

export interface TitleValidation { ok: boolean; reasonKey?: string; reasonParams?: Record<string, string | number> }

export function validateTitle(input: string, ext: string): TitleValidation {
  const t = sanitizeTitle(input)
  if (!t) return { ok: false, reasonKey: "errors.title_empty" }
  if (t.length < 3) return { ok: false, reasonKey: "errors.title_too_short" }
  if (!/[\p{L}\p{N}]/u.test(t)) return { ok: false, reasonKey: "errors.title_no_alphanum" }
  const fullLen = t.length + 1 + ext.length
  if (fullLen > MAX_TITLE_LENGTH) {
    return { ok: false, reasonKey: "errors.title_too_long", reasonParams: { max: MAX_TITLE_LENGTH - ext.length - 1 } }
  }
  return { ok: true }
}

export function defaultTitleFromFilename(filename: string): string {
  let name = filename.replace(/\.[^.]+$/, "")
  name = name.replace(/^WhatsApp Video \d{4}-\d{2}-\d{2} at [\d._]+ [APap][Mm]/, "")
  name = name.replace(/^VID[_-]\d+/i, "")
  name = name.replace(/^IMG[_-]\d+/i, "")
  name = name.replace(/^\d{8}_\d{6}/, "")
  name = name.replace(/^[-_\s]+/, "")
  name = name.replace(/[-_]+/g, " ")
  return name.trim()
}

export async function checkFilenameAvailable(title: string): Promise<boolean> {
  const fileTitle = title.startsWith("File:") ? title : "File:" + title
  const url = apiUrl + "&action=query&titles=" + encodeURIComponent(fileTitle) + "&prop=info"
  const resp = await authFetch(url)
  if (!resp.ok) throw new Error("Filename check failed: HTTP " + resp.status)
  const json = await resp.json()
  const pages = json?.query?.pages || {}
  for (const id of Object.keys(pages)) {
    if (pages[id].missing !== undefined) return true
  }
  return false
}

export async function findAvailableName(baseTitle: string, ext: string): Promise<string> {
  let candidate = baseTitle + "." + ext
  if (await checkFilenameAvailable(candidate)) return candidate
  for (let i = 2; i <= 99; i++) {
    candidate = baseTitle + " (" + i + ")." + ext
    if (await checkFilenameAvailable(candidate)) return candidate
  }
  throw new Error("Could not find available filename after 99 attempts")
}

export async function searchCategories(prefix: string): Promise<string[]> {
  const trimmed = prefix.trim()
  if (trimmed.length < 2) return []
  const url = apiUrl + "&action=query&list=allcategories&acprefix=" +
    encodeURIComponent(trimmed) + "&aclimit=10"
  const resp = await authFetch(url)
  if (!resp.ok) return []
  const json = await resp.json()
  const cats = json?.query?.allcategories || []
  return cats.map((c: { "*": string } | string) =>
    typeof c === "string" ? c : c["*"]
  ).filter(Boolean)
}

/**
 * Pre-flight check that a stash filekey is still valid before starting a
 * resume. Commons stashes filekeys for ~6 hours; older entries silently
 * disappear and a resume against them fails with a confusing "verification
 * error" mid-chunk. This API call is cheap and tells us up-front whether
 * we should restart from zero with a clear message.
 *
 * Returns true if the filekey resolves to a stashed file owned by the
 * current user; false if the server says it's gone or we couldn't verify.
 */
export async function stashFileExists(filekey: string): Promise<boolean> {
  if (!filekey) return false
  const url = apiUrl +
    "&action=query&prop=stashimageinfo&siifilekey=" + encodeURIComponent(filekey) +
    "&siiprop=size"
  try {
    const resp = await authFetch(url)
    if (!resp.ok) return false
    const json = await resp.json()
    if (json?.error) return false
    const info = json?.query?.stashimageinfo
    return Array.isArray(info) && info.length > 0 && !info[0].missing
  } catch {
    return false
  }
}

/**
 * Normalize a user-typed template into `{{Name|...}}` form, or return null
 * when the input carries no usable template name.
 *
 * Accepts "Cc-by-4.0", "{{Cc-by-4.0}}" and "{{PD-India|1900}}" alike, and
 * round-trips parameters and nested templates unchanged.
 *
 * Rejects braces-only input such as "{{}}", "{{ }}", "|" and "{{|}}".
 * MediaWiki does not treat an empty template name as a transclusion, so
 * those render as literal text on the file page - leaving the file with no
 * licence Commons can display and nothing the licence-review bot can match,
 * which is what triggers a speedy-deletion warning. Callers turn a null
 * result into their own visible fallback.
 */
function normalizeTemplate(input: string): string | null {
  const inner = input
    .trim()
    .replace(/^\{\{\s*/, "")
    .replace(/\s*\}\}$/, "")
  // The template name is everything before the first parameter separator.
  if (!inner.split("|")[0].trim()) return null
  return "{{" + inner + "}}"
}

/**
 * Map license IDs to the parameter accepted inside {{self|...}} for own-work
 * uploads. {{self}} is intended for "I am the author" cases only.
 */
function selfLicenseParam(license: LicenseId): string {
  const map: Record<LicenseId, string> = {
    "cc-by-sa-4.0": "cc-by-sa-4.0",
    "cc-by-4.0": "cc-by-4.0",
    "cc-zero": "cc-zero",
    "cc-by-sa-3.0": "cc-by-sa-3.0",
    "cc-by-3.0": "cc-by-3.0",
    "cc-by-sa-2.5": "cc-by-sa-2.5",
    "cc-by-2.5": "cc-by-2.5",
    "cc-by-sa-2.0": "cc-by-sa-2.0",
    "cc-by-2.0": "cc-by-2.0",
    "gfdl": "GFDL",
    "fal": "FAL",
    "pd-self": "PD-self"
  }
  return map[license] || license
}

/**
 * Map license IDs to the bare license template name used for third-party
 * freely-licensed content. {{cc-by-4.0}} not {{self|cc-by-4.0}}.
 */
function bareLicenseTemplate(license: LicenseId, attribution?: string): string {
  switch (license) {
    case "cc-zero":
      return "{{cc-zero}}"
    case "cc-by-sa-4.0":
    case "cc-by-4.0":
    case "cc-by-sa-3.0":
    case "cc-by-3.0":
    case "cc-by-sa-2.5":
    case "cc-by-2.5":
    case "cc-by-sa-2.0":
    case "cc-by-2.0": {
      const tpl = "{{" + license + (attribution ? "|" + attribution : "") + "}}"
      return tpl
    }
    case "gfdl":
      return "{{GFDL}}"
    case "fal":
      return "{{FAL}}"
    case "pd-self":
      // Third-party PD-self makes no sense; fall back to a generic PD tag and
      // let Commons license review correct it. UI prevents this combination.
      return "{{PD-because|reason=Released into the public domain by the original author}}"
  }
}

/** Format a Wikimedia user link, given a username string. */
function userLink(username: string): string {
  if (!username) return ""
  return "[[User:" + username + "|" + username + "]]"
}

/**
 * List the most recent video and audio files uploaded by `username` to
 * Commons. Used by the in-app "Your recent uploads" sidebar so users have
 * continuity across sessions, especially useful on mobile where tabs
 * close and users wonder "did my upload actually go through?".
 *
 * Filtering: Commons disables `aimime` (Miser Mode), so we request
 * `mediatype` via `aiprop` and filter client-side. Both VIDEO and AUDIO
 * are kept - the audio-only mode publishes Ogg files, which Commons
 * records as AUDIO, and those must still show up here or a contributor
 * would think the upload never happened. We fetch 3x the requested limit
 * to account for other media being skipped.
 *
 * Duration + thumbnails: After the initial allimages call, a follow-up
 * `prop=imageinfo` batch call fetches `duration` and a poster-frame
 * thumbnail at 240 px for each video.
 *
 * Returns up to `limit` items (capped at 50) ordered newest-first.
 */
export interface UserUpload {
  /** "File:Foo.webm" */
  title: string
  /** Full URL to the file on Commons */
  url: string
  /** Direct URL to the description page */
  pageUrl: string
  /** ISO timestamp */
  timestamp: string
  /** Bytes */
  size: number
  /** Poster-frame thumbnail URL (~240 px wide). */
  thumbUrl?: string
  /** mime type as reported by Commons */
  mime?: string
  /** Video duration in seconds (from imageinfo metadata). */
  duration?: number
}

/**
 * The videos and audio files this user most recently uploaded.
 *
 * One request, not two. This used to run list=allimages and then a second
 * prop=imageinfo over the titles it returned, which meant two serial round
 * trips to Commons before the first thumbnail could appear, plus a
 * title-normalisation map to glue the two responses back together.
 * generator=allimages feeds the same list straight into prop=imageinfo, so
 * one response carries the timestamps, sizes, mime types, mediatypes,
 * durations, description URLs and poster thumbnails together.
 *
 * The cost of a generator is ordering: pages come back keyed by page id, in
 * no useful order, so the sort the API used to do is done here.
 */
export async function listUserUploads(
  username: string,
  limit = 20
): Promise<UserUpload[]> {
  if (!username) return []
  const safeLimit = Math.max(1, Math.min(50, limit))
  // Over-fetch, because mediatype is filtered client-side: Commons offers
  // no server-side filter for it on this list (Miser Mode disables aimime).
  const fetchLimit = Math.min(50, safeLimit * 3)
  const url = apiUrl +
    "&action=query&generator=allimages" +
    "&gaiuser=" + encodeURIComponent(username) +
    "&gaisort=timestamp&gaidir=descending" +
    "&gailimit=" + fetchLimit +
    "&prop=imageinfo" +
    "&iiprop=timestamp|url|size|mime|mediatype" +
    "&iiurlwidth=240"

  const resp = await authFetch(url)
  if (!resp.ok) return []
  const json = await resp.json()
  if (json?.error) return []
  const pages = json?.query?.pages
  if (!pages || typeof pages !== "object") return []

  interface ImageInfo {
    timestamp?: string
    url?: string
    descriptionurl?: string
    size?: number
    mime?: string
    mediatype?: string
    duration?: number
    thumburl?: string
  }
  interface Page { title?: string; imageinfo?: ImageInfo[] }

  const out: UserUpload[] = []
  for (const key of Object.keys(pages)) {
    const page: Page = pages[key]
    const info = page?.imageinfo?.[0]
    if (!info) continue
    // Only the media types this tool can produce.
    if (info.mediatype !== "VIDEO" && info.mediatype !== "AUDIO") continue
    const title = page.title || ""
    if (!title) continue
    out.push({
      title,
      url: info.url || "",
      pageUrl: info.descriptionurl ||
        ("https://commons.wikimedia.org/wiki/" + encodeURIComponent(title)),
      timestamp: info.timestamp || "",
      size: typeof info.size === "number" ? info.size : 0,
      mime: info.mime,
      duration: typeof info.duration === "number" ? info.duration : undefined,
      thumbUrl: info.thumburl || undefined
    })
  }

  // Newest first. A generator hands its pages back in page-id order.
  out.sort((a, b) => (a.timestamp < b.timestamp ? 1 : a.timestamp > b.timestamp ? -1 : 0))
  return out.slice(0, safeLimit)
}

/**
 * Look up Commons files matching a given SHA-1. Used for pre-upload
 * duplicate detection: if the user picks a video whose exact bytes are
 * already on Commons, we can warn before they spend minutes encoding.
 *
 * Returns matching files (usually 0 or 1; can theoretically be more if
 * the same bytes were uploaded under multiple names). Each match
 * includes its title and description page URL.
 *
 * Caveat: only matches EXACT byte identity. A re-encoded version of the
 * same source video will have a different SHA-1 and slip through.
 */
export interface DuplicateMatch {
  title: string
  pageUrl: string
  uploader?: string
  timestamp?: string
}

export async function findBySha1(sha1Hex: string): Promise<DuplicateMatch[]> {
  if (!sha1Hex || !/^[0-9a-f]{40}$/i.test(sha1Hex)) return []
  const url = apiUrl +
    "&action=query&list=allimages" +
    "&aisha1=" + encodeURIComponent(sha1Hex.toLowerCase()) +
    "&aiprop=timestamp|user|url" +
    "&ailimit=5"
  try {
    const resp = await authFetch(url)
    if (!resp.ok) return []
    const json = await resp.json()
    const list = json?.query?.allimages
    if (!Array.isArray(list)) return []
    return list.map((item: {
      name: string
      title?: string
      descriptionurl?: string
      user?: string
      timestamp?: string
    }) => ({
      title: item.title || ("File:" + item.name),
      pageUrl: item.descriptionurl || ("https://commons.wikimedia.org/wiki/" +
        encodeURIComponent("File:" + item.name)),
      uploader: item.user,
      timestamp: item.timestamp
    }))
  } catch {
    return []
  }
}

/**
 * Render the |description= field of {{Information}}.
 *
 * Commons carries a multilingual caption as a stack of `{{<lang>|1=<text>}}`
 * templates concatenated with no separator, which is what UploadWizard
 * produces. Entries with no text are dropped, and a file is never published
 * with a blank description: if nothing usable is left, the title is used
 * under {{en}}.
 */
/**
 * Neutralise free text that is about to sit inside a template parameter.
 * A stray pipe would split the parameter and a stray brace or bracket would
 * start a template or a link, so each becomes something inert that still
 * renders as the character the contributor typed.
 */
function escapeTemplateValue(s: string): string {
  // Braces and brackets first: {{!}} is itself made of braces, so
  // escaping pipes before them turns the replacement into &#123;&#123;!...
  return s
    .replace(/\{/g, "&#123;").replace(/\}/g, "&#125;")
    .replace(/\[/g, "&#91;").replace(/\]/g, "&#93;")
    .replace(/\|/g, "{{!}}")
}

function buildDescriptionField(form: UploadForm): string {
  const escapePipes = (s: string) => s.replace(/\|/g, "{{!}}")
  const sanitizeLang = (lang: string) =>
    String(lang || "en").toLowerCase().replace(/[^a-z-]/g, "") || "en"

  // Defensive on the shape, not just the content: this runs on the upload
  // path, and a malformed entry here would throw between the encode and the
  // upload - losing the encode. A bad entry is skipped instead.
  const parts = (form.descriptions || [])
    .filter(d => d && typeof d.text === "string" && d.text.trim())
    .map(d => "{{" + sanitizeLang(d.lang) + "|1=" + escapePipes(d.text.trim()) + "}}")

  if (parts.length === 0) {
    return "{{en|1=" + escapePipes(form.title) + "}}"
  }
  return parts.join("")
}

/**
 * How long a captions edit may take - the same deadline the Wikidata requests
 * use (wikidata-api.ts). Long enough for a lagged Wikibase write, short enough
 * that a stalled one cannot hold an already-published upload on screen.
 */
const CAPTIONS_TIMEOUT_MS = 20_000

/**
 * Write structured-data captions onto a freshly uploaded file.
 *
 * Commons stores captions as labels on the file's MediaInfo entity, not in
 * the wikitext - they are what the file page shows under "Captions", and
 * they are what UploadWizard writes from its own caption field. So this
 * runs AFTER the upload, as a separate Wikibase edit.
 *
 * The entity is addressed by `site` + `title` rather than by id, which
 * avoids a round trip to look the pageid up. One wbeditentity call carries
 * every language at once.
 *
 * Callers must treat a failure here as a warning, never as an upload
 * failure: by the time this runs the file is already published, and the
 * contributor can add captions on-wiki afterwards.
 */
export async function setCaptions(
  filename: string,
  captions: Array<{ lang: string; text: string }>,
  csrfToken: string
): Promise<void> {
  const labels: Record<string, { language: string; value: string }> = {}
  for (const c of captions) {
    if (!c || typeof c.text !== "string") continue
    const value = c.text.trim()
    const language = String(c.lang || "").trim()
    if (!value || !language) continue
    // Last one wins if a language somehow appears twice.
    labels[language] = { language, value }
  }
  if (Object.keys(labels).length === 0) return

  const title = filename.startsWith("File:") ? filename : "File:" + filename
  const body = new FormData()
  body.append("action", "wbeditentity")
  // Addressing the entity by site+title avoids a round trip to resolve the
  // pageid into an "M<pageid>" MediaInfo entity id.
  body.append("site", "commonswiki")
  body.append("title", title)
  body.append("token", csrfToken)
  body.append("data", JSON.stringify({ labels }))
  body.append("summary", "Adding captions with clip2commons")

  // Same endpoint the upload itself posts to, so format/maxlag handling and
  // the OAuth bearer header stay identical.
  //
  // Bounded: this runs after the file is published but before the upload
  // screen switches to "done", so an unanswered request would leave a
  // finished upload looking stuck. The abort cancels the request; the race is
  // what actually bounds the wait, because authFetch can be waiting on a token
  // refresh that the abort signal does not reach.
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const deadline = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error("Captions failed: Commons took too long to answer."))
      controller.abort()
    }, CAPTIONS_TIMEOUT_MS)
  })
  const save = (async () => {
    const resp = await authFetch(apiUrl, { method: "POST", body, signal: controller.signal })
    if (!resp.ok) throw new Error("Captions failed: HTTP " + resp.status)
    const json = await resp.json()
    if (json.error) {
      throw new Error("Captions failed: " + (json.error.info || json.error.code))
    }
    if (json.success !== 1) {
      throw new Error("Captions were not saved.")
    }
  })()
  try {
    // The loser of the race is still observed by Promise.race, so a request
    // that fails after the deadline cannot surface as an unhandled rejection.
    await Promise.race([save, deadline])
  } finally {
    clearTimeout(timer)
  }
}

export function buildWikitext(form: UploadForm, currentUsername: string): string {
  const cats = (form.categories || []).filter(c => c.trim())

  let descriptionBody = buildDescriptionField(form)

  let sourceLine: string
  let authorLine: string
  let permissionLine = "|permission="
  let licenseSection: string
  const extraCategories: string[] = []

  if (form.provenance.kind === "own") {
    sourceLine = "|source={{own}}"
    const author = form.authorOverride.trim()
    authorLine =
      "|author=" + (author ? author : userLink(currentUsername))
    licenseSection = ownLicenseSection(form.provenance.license)
    if (form.provenance.ownership.kind === "ai-generated") {
      // AI-generated own work: tag the file so reviewers know.
      extraCategories.push("AI-generated media")
      // {{PD-algorithm}} is the conventional template for AI output that
      // is not eligible for copyright in the US.
      licenseSection = "{{PD-algorithm}}\n" + licenseSection

      // Commons:AI-generated media asks for "the name of the specific AI
      // engine used, followed by the name of the person who created the
      // prompt", and encourages - does not require - recording the prompt
      // in the file description. Both are optional, so the wikitext is
      // byte-for-byte what it was before whenever they are left blank. An
      // explicit author override still wins: it is the more specific
      // instruction from the person uploading.
      const tool = (form.provenance.ownership.aiTool || "").trim()
      const prompt = (form.provenance.ownership.aiPrompt || "").trim()
      if (tool && !author) {
        authorLine = "|author=" + escapeTemplateValue(tool) +
          "; prompted by " + userLink(currentUsername)
      }
      if (prompt) {
        descriptionBody += "\n\n''Prompt:'' " + escapeTemplateValue(prompt)
      }
    }
  } else {
    // Third-party. Each branch builds its own license section + may add
    // a |permission= line (VRT for the "permission" branch).
    const url = form.provenance.sourceUrl.trim()
    sourceLine = "|source=" + (url ? url : "")
    const orig = form.provenance.originalAuthor.trim()
    authorLine = "|author=" + (form.provenance.unknownAuthor ? "{{unknown|author}}" : (orig || ""))

    switch (form.provenance.reason) {
      case "creator-released":
        licenseSection = renderLicenseChoice(form.provenance.license, orig) +
          "\n{{Information_field|name=Uploaded by|value=" + userLink(currentUsername) + "}}"
        break
      case "heir":
        licenseSection = renderLicenseChoice(form.provenance.license, orig) +
          "\n{{Information_field|name=Uploaded by|value=" + userLink(currentUsername) +
          " (heir to the original author)}}"
        break
      case "not-protected": {
        const tags: string[] = []
        const reasons = form.provenance.pdReasons || []
        if (reasons.includes("us-pre-1931")) tags.push("{{PD-US-expired}}")
        if (reasons.includes("author-deceased-70")) tags.push("{{PD-old-70}}")
        if (reasons.includes("us-federal")) tags.push("{{PD-USGov}}")
        if (reasons.includes("nasa")) tags.push("{{PD-USGov-NASA}}")
        // Custom PD tag input - normalized to {{Name|...}} form, accepting
        // either a bare template name or an already-braced one. A tag with
        // no usable template name is ignored entirely, so the
        // {{PD-because}} fallback below still fires and the file is never
        // left without a visible public-domain tag.
        const customTag = normalizeTemplate(form.provenance.pdTag || "")
        if (customTag) {
          tags.push(customTag)
        }
        // Always include {{PD-because}} as a fallback when nothing else applies.
        licenseSection = tags.length > 0
          ? tags.join("\n")
          : "{{PD-because|reason=Public domain (uploader did not specify a tag)}}"
        licenseSection += "\n{{Information_field|name=Uploaded by|value=" +
          userLink(currentUsername) + "}}"
        break
      }
      case "permission":
        permissionLine = "|permission={{Permission|OTRS pending verification}}"
        licenseSection = renderLicenseChoice(form.provenance.license, orig) +
          "\n{{Information_field|name=Uploaded by|value=" + userLink(currentUsername) + "}}"
        // Mark for VRT review category so admins can find pending items.
        extraCategories.push("Items pending OTRS confirmation of permission as of " +
          new Date().toISOString().substring(0, 10))
        break
      case "unknown":
      case null:
      default:
        // Should be blocked at the form level; emit a placeholder that will
        // fail Commons review so this never silently slips through.
        licenseSection = "{{copyvio|reason=License not selected - uploaded in error}}"
        break
    }

    if (form.provenance.isAiGenerated) {
      extraCategories.push("AI-generated media")
    }
  }

  const allCategories = [
    ...cats.map(c => "[[Category:" + c.trim() + "]]"),
    ...extraCategories.map(c => "[[Category:" + c + "]]")
  ].join("\n")

  return `=={{int:filedesc}}==
{{Information
|description=${descriptionBody}
|date=${form.date}
${sourceLine}
${authorLine}
${permissionLine}
|other versions=
}}

=={{int:license-header}}==
${licenseSection}

[[Category:Uploaded with clip2commons]]
${allCategories}
`
}

/** Build the license section for own-work uploads. */
function ownLicenseSection(license: LicenseChoice): string {
  if (license.kind === "preset") {
    // Own work with a standard CC/PD preset → {{self|<param>}}. The author
    // is carried separately in the Information |author= field, so {{self}}
    // only needs the license parameter here.
    return "{{self|" + selfLicenseParam(license.id) + "}}"
  }
  if (license.kind === "custom") {
    // Accept both "Cc-by-sa-4.0" and "{{Cc-by-sa-4.0}}". Input with no usable
    // template name gets the same visible blocking template as empty input,
    // rather than an inert "{{}}" that Commons renders as plain text.
    const tag = normalizeTemplate(license.template)
    if (!tag) {
      return "{{copyvio|reason=Custom license template was empty}}"
    }
    return tag
  }
  // "unknown" should not be reachable for own-work; the UI prevents it.
  return "{{copyvio|reason=License not selected}}"
}

/** Build the license section for third-party uploads. */
function renderLicenseChoice(
  license: LicenseChoice | undefined,
  attribution: string
): string {
  if (!license) return "{{copyvio|reason=License not selected}}"
  if (license.kind === "preset") {
    return bareLicenseTemplate(license.id, attribution)
  }
  if (license.kind === "custom") {
    const tag = normalizeTemplate(license.template)
    if (!tag) return "{{copyvio|reason=Custom license template was empty}}"
    return tag
  }
  // "unknown" - the file gets uploaded but flagged for license review by
  // a human, matching UploadWizard's behavior for the same choice.
  return "{{subst:nld}}"
}

/**
 * State emitted to the caller after each successful chunk. Persisting this
 * to IndexedDB lets us resume after page reload / network drop without
 * re-uploading earlier chunks (Feature #2).
 */
export interface ChunkCheckpoint {
  filekey: string
  offset: number
  totalBytes: number
}

/**
 * Thrown by chunkedUpload when the server's finalize response includes
 * warnings (duplicate file, badfilename, exists, etc.). The caller can
 * present the warnings to the user, get an explicit "yes, upload anyway"
 * confirmation, then retry chunkedUpload with `confirmWarnings: true`.
 *
 * Caller can also retry with the same resumeFrom to skip re-uploading
 * already-stashed bytes - only the finalize step needs to repeat.
 */
export class UploadWarningsError extends Error {
  /** The raw warnings object from the API. Keys are warning codes
   *  ('duplicate', 'exists', 'was-deleted', 'thumb', 'badfilename',
   *  'large-file', etc.); values are the offending data. */
  warnings: Record<string, unknown>
  /** The stash filekey we ended up with. The same filekey can be passed back
   *  via resumeFrom on retry to skip re-uploading the bytes. */
  filekey: string
  constructor(warnings: Record<string, unknown>, filekey: string) {
    super("Upload finalize returned warnings: " + Object.keys(warnings).join(", "))
    this.name = "UploadWarningsError"
    this.warnings = warnings
    this.filekey = filekey
  }
}

/**
 * The bytes to upload: the encoder output, or the source file itself on a
 * passthrough. Blob.slice() is lazy, so a passthrough never needs the
 * whole file resident - which is what lets the ceiling go to 2 GB.
 */
export type UploadPayload = ArrayBuffer | Blob

/** Size of either payload kind. */
export function payloadSize(bytes: UploadPayload): number {
  return bytes instanceof Blob ? bytes.size : bytes.byteLength
}

export interface UploadOptions {
  bytes: UploadPayload
  filename: string
  csrfToken: string
  wikitext: string
  signal?: AbortSignal
  onProgress: (p: ChunkProgress) => void
  /**
   * Optional starting point for resume. When set, the chunk loop begins at
   * `resumeFrom.offset` and reuses the existing stash filekey instead of
   * starting fresh.
   */
  resumeFrom?: { filekey: string; offset: number } | null
  /**
   * Called after each successful chunk with the latest filekey + offset.
   * Use this to persist resume state. Errors thrown here are swallowed.
   */
  onChunkComplete?: (cp: ChunkCheckpoint) => void | Promise<void>
  /**
   * If true, finalize the upload with `ignorewarnings=1` after the user has
   * explicitly acknowledged the warnings from a previous attempt. Default
   * false - first attempt always lets warnings surface for user review.
   */
  confirmWarnings?: boolean
  /** Optional change-tag to apply (must be registered on Commons). */
  tag?: string
}

export async function chunkedUpload(opts: UploadOptions): Promise<UploadResult> {
  const {
    bytes, filename, csrfToken, wikitext, signal, onProgress,
    resumeFrom, onChunkComplete, confirmWarnings, tag
  } = opts
  const totalBytes = payloadSize(bytes)
  if (totalBytes === 0) throw new Error("Upload aborted: file size is 0 bytes (encoder bug?)")

  let filekey: string | undefined = resumeFrom?.filekey
  let offset = resumeFrom?.offset ?? 0
  if (offset < 0 || offset > totalBytes) offset = 0
  let chunkIndex = Math.floor(offset / UPLOAD_CHUNK_SIZE)
  const chunkCount = Math.ceil(totalBytes / UPLOAD_CHUNK_SIZE)
  const startTime = performance.now()

  // Surface the already-present progress immediately (so the UI shows the
  // resume jumpstart instead of going from 0).
  if (offset > 0) {
    onProgress({ ratio: offset / totalBytes, uploadedBytes: offset, totalBytes })
  }

  while (offset < totalBytes) {
    if (signal?.aborted) throw new DOMException("aborted", "AbortError")
    const end = Math.min(offset + UPLOAD_CHUNK_SIZE, totalBytes)
    // Blob.slice() hands back a view the browser reads on demand;
    // ArrayBuffer.slice() copies the 5 MB. Either way only one chunk is
    // ever in flight.
    const chunk = bytes.slice(offset, end)

    const fd = new FormData()
    fd.append("action", "upload")
    fd.append("format", "json")
    fd.append("filename", filename)
    fd.append("token", csrfToken)
    fd.append("stash", "1")
    fd.append("offset", String(offset))
    fd.append("filesize", String(totalBytes))
    // NOTE: no ignorewarnings on chunk uploads. Per-chunk warnings (mostly
    // size/format issues) should fail loudly so the user can address them
    // rather than be papered over.
    if (filekey) fd.append("filekey", filekey)
    fd.append("chunk", new Blob([chunk]), filename)

    const json = await uploadChunkWithRetry(fd, signal)
    if (json.error) {
      throw new Error("Upload chunk " + (chunkIndex + 1) + "/" + chunkCount + " failed: " +
        (json.error.info || json.error.code))
    }
    if (json?.upload?.filekey) filekey = json.upload.filekey
    offset = end
    chunkIndex++
    onProgress({ ratio: offset / totalBytes, uploadedBytes: offset, totalBytes })

    if (filekey && onChunkComplete) {
      try { await onChunkComplete({ filekey, offset, totalBytes }) }
      catch { /* persisting is best-effort */ }
    }
  }

  if (!filekey) throw new Error("Upload completed but no filekey received")

  const fd = new FormData()
  fd.append("action", "upload")
  fd.append("format", "json")
  fd.append("filename", filename)
  fd.append("token", csrfToken)
  fd.append("filekey", filekey)
  fd.append("comment", "Uploaded with clip2commons")
  fd.append("text", wikitext)
  if (confirmWarnings) fd.append("ignorewarnings", "1")
  if (tag) fd.append("tags", tag)

  const finalResp = await fetch(apiUrl, { method: "POST", body: fd, headers: authHeaders(), signal })
  if (!finalResp.ok) throw new Error("Finalize failed: HTTP " + finalResp.status)
  const finalJson = await finalResp.json()
  if (finalJson.error) throw new Error("Finalize failed: " + (finalJson.error.info || finalJson.error.code))

  // Warnings: not an error per se, but the user should explicitly acknowledge
  // them before we re-finalize with ignorewarnings=1. Throw a typed error so
  // App.vue can present a confirm dialog and retry with confirmWarnings:true.
  const upload = finalJson?.upload
  if (upload && upload.result === "Warning" && upload.warnings) {
    const warnings = upload.warnings as Record<string, unknown>
    const warningFilekey = (upload.filekey as string | undefined) || filekey
    throw new UploadWarningsError(warnings, warningFilekey)
  }

  if (!upload || upload.result !== "Success") {
    throw new Error("Upload finalize did not return Success: " +
      (upload?.result || JSON.stringify(finalJson)))
  }

  const elapsed = performance.now() - startTime
  console.log("[c2c] upload complete in " + Math.round(elapsed) + "ms")

  const finalFilename = upload.filename || filename
  return {
    filename: finalFilename,
    fileUrl: upload.imageinfo?.url || "",
    pageUrl: "https://commons.wikimedia.org/wiki/File:" + encodeURIComponent(finalFilename),
    totalBytes, encodeMs: 0, audioIncluded: false, passthrough: false
  }
}

// A 5 MiB chunk can legitimately take several minutes on a poor mobile
// connection. This guards only requests that otherwise never settle; failed
// attempts still use the existing resumable retry path below.
const CHUNK_REQUEST_TIMEOUT_MS = 5 * 60 * 1000

/**
 * Retry a chunk POST with exponential backoff. Increased from 3 to 6 retries
 * because 3G hand-offs and brief Cloudflare 5xx blips on Commons can chew up
 * the budget on long uploads. Backoff is capped at 30 seconds.
 *
 * Respects Retry-After (when the server provides one) instead of our own
 * formula, and surfaces 429 (rate limit) cleanly.
 */
async function uploadChunkWithRetry(
  fd: FormData, signal?: AbortSignal, maxRetries = 6
): Promise<{
  error?: { code?: string; info?: string }
  upload?: { filekey?: string; result?: string; filename?: string; imageinfo?: { url?: string } }
}> {
  let lastErr: Error | undefined
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) throw new DOMException("aborted", "AbortError")
    const requestController = new AbortController()
    let timedOut = false
    const onParentAbort = () => requestController.abort(signal?.reason)
    signal?.addEventListener("abort", onParentAbort, { once: true })
    const timeoutId = setTimeout(() => {
      timedOut = true
      requestController.abort()
    }, CHUNK_REQUEST_TIMEOUT_MS)
    try {
      const resp = await fetch(apiUrl, {
        method: "POST", body: fd, headers: authHeaders(), signal: requestController.signal
      })
      if (!resp.ok) {
        if ((resp.status === 429 || resp.status >= 500) && attempt < maxRetries) {
          await backoff(resp, attempt, signal); continue
        }
        throw new Error("HTTP " + resp.status)
      }
      const json = await resp.json()
      if (json.error && /(maxlag|servererror|internal_api_error|ratelimit)/i.test(json.error.code || "") &&
        attempt < maxRetries) {
        await backoff(resp, attempt, signal); continue
      }
      return json
    } catch (e) {
      if (signal?.aborted) throw new DOMException("aborted", "AbortError")
      if (timedOut) lastErr = new Error("Upload chunk request timed out")
      else lastErr = e instanceof Error ? e : new Error(String(e))
      if (attempt < maxRetries) { await sleep(Math.min(30_000, 1000 * 2 ** (attempt - 1))); continue }
    } finally {
      clearTimeout(timeoutId)
      signal?.removeEventListener("abort", onParentAbort)
    }
  }
  throw lastErr || new Error("Upload chunk failed after " + maxRetries + " retries")
}

/** Sleep for Retry-After if the server gave one, otherwise exponential
 *  backoff capped at 30s. Aborts cleanly via the signal. */
async function backoff(resp: Response, attempt: number, signal?: AbortSignal): Promise<void> {
  let waitMs = Math.min(30_000, 1000 * 2 ** (attempt - 1))
  const ra = resp.headers.get("Retry-After")
  if (ra) {
    const sec = parseInt(ra, 10)
    if (Number.isFinite(sec) && sec > 0) waitMs = Math.min(60_000, sec * 1000)
  }
  await new Promise<void>((res, rej) => {
    const tid = setTimeout(res, waitMs)
    if (signal) {
      signal.addEventListener("abort", () => {
        clearTimeout(tid); rej(new DOMException("aborted", "AbortError"))
      }, { once: true })
    }
  })
}

function sleep(ms: number): Promise<void> { return new Promise(r => setTimeout(r, ms)) }

// Re-exported for convenience: callers can import getTranscodeStatus straight
// from commons-api if they prefer a single import surface.
export { getTranscodeStatus } from "./transcode-status"
export type { TranscodeEntry, TranscodeStatusResult, TranscodeJobStatus } from "./types"
